import { Component, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../../services/auth/auth.service';
import { normalizeApiErrors } from '../../../utils/api-errors.util';
import { isInvalidTokenFailure } from '../../../utils/reset-password-outcome.util';

/**
 * Shape written into the navigation state by the two producers of this page:
 * `ResetPasswordComponent.submitForm()` (the outcome of a submitted reset) and
 * `isTokenValidGuard` (a link that failed pre-validation).
 *
 * Every field is `unknown` on purpose. This is read back out of `history.state`,
 * which is browser-owned storage: it survives a reload, it carries Angular's own
 * `navigationId` / `ɵrouterPageId` keys alongside ours, and on a cold visit it
 * may hold a state written by an entirely different page. Narrow before trusting.
 */
interface ResetPasswordStatusState {
  status?: unknown;
  message?: unknown;
}

@Component({
  selector: 'reset-password-status',
  imports: [RouterLink],
  templateUrl: './reset-password-status.component.html',
})
export class ResetPasswordStatusComponent {
  status = signal<boolean>(false);
  invalidToken = signal<boolean>(false);
  router = inject(Router);
  authService = inject(AuthService);

  constructor() {
    this.resolveStatus();
  }

  /**
   * Resolve which of the three screens to render, in priority order:
   *
   *   1. `history.state` — the outcome handed over by whichever page navigated
   *      here.
   *   2. `AuthService.isTokenValid()` — the guard's fallback channel, which is
   *      `null` unless a link failed pre-validation during this app lifetime.
   *   3. Nothing to report at all → send the visitor to the login page.
   *
   * Why `history.state` and NOT `router.getCurrentNavigation()`: this component
   * is constructed by the `<router-outlet>` inside `AuthLayoutComponent`, and
   * that outlet only activates on the change-detection pass AFTER the initial
   * navigation completes. So on any cold load of this URL — including the
   * `window.location.reload()` that `AuthLayoutComponent` performs when a
   * sibling tab signs in — `getCurrentNavigation()` has already returned to
   * `null` by the time we run, and every successful reset silently rendered
   * "Password Reset Failed". `history.state` holds the same object and survives
   * both the reload and a back/forward navigation.
   */
  private resolveStatus(): void {
    const state = history.state as ResetPasswordStatusState | null;

    // Only a real boolean counts. Angular always writes SOMETHING into
    // `history.state` (`navigationId`), so a truthiness test on the object would
    // treat every unrelated history entry as a reset outcome.
    if (typeof state?.status === 'boolean') {
      this.setStatus(state.status, normalizeApiErrors(state.message));
      return;
    }

    const fallbackStatus = this.authService.isTokenValid();
    if (fallbackStatus !== null) {
      this.setStatus(
        fallbackStatus,
        normalizeApiErrors(this.authService.isTokenValidError())
      );
      return;
    }

    this.router.navigate(['/auth/login']);
  }

  /**
   * `messages` arrives as a normalised `string[]` — the producers hand over
   * `ApiResponse.errors`, which is typed `string[]` but can be a bare string on
   * the wire, so it must come through `normalizeApiErrors` at every call site.
   * The previous signature declared `message: string` while actually receiving
   * the array, which turned the intended substring test into
   * `Array.prototype.includes` doing an exact-element match — a test no real
   * backend message could ever satisfy.
   */
  private setStatus(status: boolean, messages: string[]): void {
    this.status.set(status);

    if (status) return;

    this.invalidToken.set(isInvalidTokenFailure(messages));
  }
}
